import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { Alert, Button, Card, Typography } from '@material-tailwind/react';
import {
  FaBell,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaFilter,
  FaRegClock,
  FaSearch,
  FaTasks,
  FaTrash,
  FaRedo,
  FaGlobe,
  FaUserShield
} from 'react-icons/fa';
import { useAuth } from './AuthContext';

const { rootUrl } = require('../../config/config');

const PAGE_SIZE = 12;

const statusOptions = [
  { value: 'all', label: 'All' },
  { value: 'unread', label: 'Unread' },
  { value: 'read', label: 'Read' }
];

const sourceOptions = [
  { value: 'all', label: 'All sources' },
  { value: 'task', label: 'Tasks' },
  { value: 'email', label: 'Email' },
  { value: 'system', label: 'System' }
];

const sourceStyles = {
  task: 'bg-amber-50 text-amber-700 border-amber-200',
  email: 'bg-blue-50 text-blue-700 border-blue-200',
  system: 'bg-red-50 text-red-700 border-red-200',
  default: 'bg-slate-50 text-slate-700 border-slate-200'
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '42px',
    borderColor: state.isFocused ? '#f59e0b' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 1px #f59e0b' : 'none',
    '&:hover': {
      borderColor: '#f59e0b'
    }
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
};

const formatTimeAgo = (value) => {
  if (!value) {
    return 'Just now';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return 'Just now';
  }

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(value).toLocaleDateString();
};

const getSourceLabel = (source) => {
  if (!source) return 'System';
  return source.charAt(0).toUpperCase() + source.slice(1);
};

const getScopeLabel = (notification) => {
  if (!notification) return 'Personal';
  if (notification.recipient_scope === 'all') return 'Everyone';
  if (notification.recipient_scope === 'role') {
    if (notification.recipient_key === 'roles.manage') return 'Admins';
    return notification.recipient_key || 'Role';
  }
  return 'Personal';
};

const getNotificationIcon = (notification) => {
  const source = notification?.source || 'system';
  const type = notification?.notification_type || '';

  if (source === 'email') return FaEnvelope;
  if (source === 'task' || type.includes('task')) return FaTasks;
  if (type.includes('expense')) return FaCheckCircle;
  if (type.includes('failed') || type.includes('alert')) return FaExclamationTriangle;
  return FaBell;
};

const getNotificationDestination = (notification) => {
  if (!notification) return null;
  if (notification.metadata?.route) return notification.metadata.route;
  if (notification.entity_type === 'expense' && notification.entity_id) {
    return `/expenses/${notification.entity_id}`;
  }
  return null;
};

const NotificationsPage = () => {
  const { token, isAuthenticated } = useAuth();
  const selectPortalTarget = typeof document !== 'undefined' ? document.body : null;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    pageSize: PAGE_SIZE
  });
  const [filters, setFilters] = useState({
    status: 'all',
    source: 'all',
    search: ''
  });

  const loadUnreadCount = useCallback(async () => {
    const accessToken = token || localStorage.getItem('authToken');
    if (!accessToken) {
      setUnreadCount(0);
      return;
    }

    try {
      const response = await fetch(`${rootUrl}/api/notifications/unread-count`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.data?.unread_count || 0);
      }
    } catch (err) {
      setUnreadCount(0);
    }
  }, [token]);

  const loadNotifications = useCallback(async (page = 1) => {
    const accessToken = token || localStorage.getItem('authToken');
    if (!accessToken) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      params.set('status', filters.status);
      params.set('source', filters.source);
      if (filters.search.trim()) {
        params.set('search', filters.search.trim());
      }

      const response = await fetch(`${rootUrl}/api/notifications?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to load notifications');
      }

      setNotifications(data.data || []);
      setPagination(data.pagination || {
        currentPage: page,
        totalPages: 1,
        totalRecords: 0,
        pageSize: PAGE_SIZE
      });
    } catch (err) {
      setError(err.message || 'Failed to load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    loadNotifications(1);
    loadUnreadCount();
  }, [filters, loadNotifications, loadUnreadCount]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return undefined;
    }

    let stream = null;
    try {
      stream = new EventSource(`${rootUrl}/api/notifications/stream?token=${encodeURIComponent(token)}`);
      stream.addEventListener('notification', () => {
        loadNotifications(pagination.currentPage);
        loadUnreadCount();
      });
      stream.addEventListener('ready', () => {
        loadUnreadCount();
      });
    } catch (err) {
      stream = null;
    }

    return () => {
      if (stream) {
        stream.close();
      }
    };
  }, [token, isAuthenticated, loadNotifications, loadUnreadCount, pagination.currentPage]);

  const handleMarkRead = async (id) => {
    const accessToken = token || localStorage.getItem('authToken');
    if (!accessToken) return;

    try {
      setSaving(true);
      const response = await fetch(`${rootUrl}/api/notifications/${id}/read`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to mark notification as read');
      }

      await Promise.all([
        loadNotifications(pagination.currentPage),
        loadUnreadCount()
      ]);
    } catch (err) {
      setError(err.message || 'Failed to update notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDismiss = async (id) => {
    const accessToken = token || localStorage.getItem('authToken');
    if (!accessToken) return;

    try {
      setSaving(true);
      const response = await fetch(`${rootUrl}/api/notifications/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to dismiss notification');
      }

      await Promise.all([
        loadNotifications(pagination.currentPage),
        loadUnreadCount()
      ]);
    } catch (err) {
      setError(err.message || 'Failed to update notification');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkAllRead = async () => {
    const accessToken = token || localStorage.getItem('authToken');
    if (!accessToken) return;

    try {
      setSaving(true);
      const response = await fetch(`${rootUrl}/api/notifications/read-all`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to mark all notifications as read');
      }

      await Promise.all([
        loadNotifications(pagination.currentPage),
        loadUnreadCount()
      ]);
    } catch (err) {
      setError(err.message || 'Failed to update notifications');
    } finally {
      setSaving(false);
    }
  };

  const totalVisible = pagination.totalRecords || notifications.length;
  const unreadVisible = notifications.filter((item) => !item.is_read).length;

  return (
    <div className="min-h-full bg-gradient-to-br from-[#fff8f2] via-white to-[#f4efe8]">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 overflow-hidden rounded-3xl border border-amber-200/70 bg-white/80 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-[#2a2e30] via-[#44413b] to-[#8b5a2b] px-5 py-5 text-white sm:px-7">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-amber-100">
                  <FaBell /> Activity center
                </div>
                <h1 className="text-3xl font-bold sm:text-4xl">Notifications</h1>
                <p className="mt-2 max-w-2xl text-sm text-amber-50/90 sm:text-base">
                  Track task updates, email failures, and system alerts in one live feed.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  color="amber"
                  className="flex items-center gap-2 bg-amber-500 text-[#2a2e30] shadow-none hover:bg-amber-400"
                  onClick={handleMarkAllRead}
                  disabled={saving || unreadCount === 0}
                >
                  <FaCheckCircle />
                  Mark all read
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-4 border-b border-amber-100 px-5 py-5 sm:grid-cols-2 xl:grid-cols-4">
            <Card className="border border-slate-200 p-4 shadow-none">
              <Typography variant="small" className="text-slate-500">
                Unread alerts
              </Typography>
              <Typography variant="h4" className="mt-2 text-slate-900">
                {unreadCount}
              </Typography>
            </Card>
            <Card className="border border-slate-200 p-4 shadow-none">
              <Typography variant="small" className="text-slate-500">
                Visible alerts
              </Typography>
              <Typography variant="h4" className="mt-2 text-slate-900">
                {totalVisible}
              </Typography>
            </Card>
            <Card className="border border-slate-200 p-4 shadow-none">
              <Typography variant="small" className="text-slate-500">
                Current page
              </Typography>
              <Typography variant="h4" className="mt-2 text-slate-900">
                {pagination.currentPage}/{pagination.totalPages || 1}
              </Typography>
            </Card>
            <Card className="border border-slate-200 p-4 shadow-none">
              <Typography variant="small" className="text-slate-500">
                Unread on page
              </Typography>
              <Typography variant="h4" className="mt-2 text-slate-900">
                {unreadVisible}
              </Typography>
            </Card>
          </div>

          <div className="space-y-4 px-5 py-5">
            {error && (
              <Alert color="red" className="rounded-2xl">
                {error}
              </Alert>
            )}

            <div className="grid gap-3 lg:grid-cols-[1fr_220px_220px]">
              <label className="relative block">
                <span className="sr-only">Search notifications</span>
                <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  placeholder="Search title or message"
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-4 text-sm text-slate-800 shadow-sm outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                />
              </label>

              <Select
                instanceId="notification-status"
                className="text-sm"
                styles={selectStyles}
                menuPortalTarget={selectPortalTarget}
                menuPosition="fixed"
                options={statusOptions}
                value={statusOptions.find((option) => option.value === filters.status)}
                onChange={(option) => setFilters((prev) => ({ ...prev, status: option?.value || 'all' }))}
              />

              <Select
                instanceId="notification-source"
                className="text-sm"
                styles={selectStyles}
                menuPortalTarget={selectPortalTarget}
                menuPosition="fixed"
                options={sourceOptions}
                value={sourceOptions.find((option) => option.value === filters.source)}
                onChange={(option) => setFilters((prev) => ({ ...prev, source: option?.value || 'all' }))}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                <FaFilter className="text-xs" />
                Live filters active
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                <FaGlobe className="text-xs" />
                Realtime stream enabled
              </span>
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1">
                <FaUserShield className="text-xs" />
                Admin alerts included
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {loading && notifications.length === 0 ? (
            <Card className="flex min-h-[280px] items-center justify-center border border-slate-200 bg-white/80 shadow-sm">
              <div className="flex flex-col items-center gap-3 text-slate-500">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-200 border-t-amber-500" />
                <p className="text-sm font-medium">Loading notifications...</p>
              </div>
            </Card>
          ) : notifications.length === 0 ? (
            <Card className="border border-dashed border-slate-300 bg-white/80 p-8 text-center shadow-sm">
              <FaBell className="mx-auto text-4xl text-slate-300" />
              <Typography variant="h5" className="mt-4 text-slate-800">
                No notifications found
              </Typography>
              <Typography className="mt-2 text-sm text-slate-500">
                Try adjusting your filters or wait for new alerts to arrive.
              </Typography>
            </Card>
          ) : (
            notifications.map((notification) => {
              const Icon = getNotificationIcon(notification);
              const sourceClass = sourceStyles[notification.source] || sourceStyles.default;
              const destination = getNotificationDestination(notification);

              return (
                <Card
                  key={notification.id}
                  className={`border transition-all duration-200 ${
                    notification.is_read
                      ? 'border-slate-200 bg-white'
                      : 'border-amber-200 bg-amber-50/60 shadow-md'
                  }`}
                >
                  <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
                    <div className="flex min-w-0 gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border ${sourceClass}`}>
                        <Icon className="text-lg" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {destination ? (
                            <Link to={destination} className="truncate text-slate-900 hover:text-[#FF5733] hover:underline">
                              <Typography variant="h6" className="truncate text-inherit">
                                {notification.title || 'Notification'}
                              </Typography>
                            </Link>
                          ) : (
                            <Typography variant="h6" className="truncate text-slate-900">
                              {notification.title || 'Notification'}
                            </Typography>
                          )}
                          {!notification.is_read && (
                            <span className="inline-flex items-center rounded-full bg-amber-500 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                              New
                            </span>
                          )}
                        </div>

                        <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-600">
                          {notification.message || 'No message provided'}
                        </p>

                        {destination && (
                          <div className="mt-3">
                            <Link to={destination} className="text-sm font-semibold text-[#FF5733] hover:underline">
                              Open expense
                            </Link>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 ${sourceClass}`}>
                            {getSourceLabel(notification.source)}
                          </span>
                          <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                            {getScopeLabel(notification)}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1">
                            <FaRegClock className="text-[10px]" />
                            {formatTimeAgo(notification.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2 sm:self-start">
                      {!notification.is_read && (
                        <Button
                          size="sm"
                          variant="outlined"
                          color="green"
                          className="flex items-center gap-2 rounded-full"
                          onClick={() => handleMarkRead(notification.id)}
                          disabled={saving}
                        >
                          <FaCheckCircle />
                          Mark read
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outlined"
                        color="red"
                        className="flex items-center gap-2 rounded-full"
                        onClick={() => handleDismiss(notification.id)}
                        disabled={saving}
                      >
                        <FaTrash />
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {pagination.totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 shadow-sm">
            <Typography variant="small" className="text-slate-600">
              Page {pagination.currentPage} of {pagination.totalPages} - {pagination.totalRecords} total alerts
            </Typography>

            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outlined"
                onClick={() => loadNotifications(Math.max(1, pagination.currentPage - 1))}
                disabled={pagination.currentPage <= 1 || loading}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="filled"
                color="amber"
                onClick={() => loadNotifications(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                disabled={pagination.currentPage >= pagination.totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm">
            <FaRedo className="text-[10px]" />
            Stream keeps the feed updated in real time
          </span>
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
